/**
 * Mirrors the backend's QuoteVerifier.Normalized so a quote located against the extracted
 * document text can be found again in what docx-preview renders: lowercase, punctuation
 * dropped, whitespace runs collapsed to one space.
 *
 * Punctuation sitting between two digits is kept verbatim, because it is numerically
 * significant — without it "3.14" and "314" normalize alike.
 *
 * Runs over a whole document on every load, so characters are classified by table lookup
 * rather than by a regex per character.
 */

const OTHER = 0;
const SPACE = 1;
const DIGIT = 2;
const LETTER = 3;

const ASCII_LIMIT = 128;

const ASCII_CLASS = new Uint8Array(ASCII_LIMIT);

for (let code = 0; code < ASCII_LIMIT; code += 1) {
  const character = String.fromCharCode(code);

  if (/\s/.test(character)) {
    ASCII_CLASS[code] = SPACE;
  } else if (character >= "0" && character <= "9") {
    ASCII_CLASS[code] = DIGIT;
  } else if (/[a-z]/i.test(character)) {
    ASCII_CLASS[code] = LETTER;
  }
}

const wideClassCache = new Map();

// docx-preview leaves the non-breaking spaces the backend already turned into plain
// spaces at parse time, so treating them as whitespace is what keeps the two in step.
const classify = (character) => {
  const code = character.charCodeAt(0);

  if (code < ASCII_LIMIT) {
    return ASCII_CLASS[code];
  }

  const cached = wideClassCache.get(character);

  if (cached !== undefined) {
    return cached;
  }

  let characterClass = OTHER;

  if (/\s/u.test(character)) {
    characterClass = SPACE;
  } else if (/\p{Nd}/u.test(character)) {
    characterClass = DIGIT;
  } else if (/[\p{L}\p{N}]/u.test(character)) {
    characterClass = LETTER;
  }

  wideClassCache.set(character, characterClass);

  return characterClass;
};

const normalizeAnchorText = (text = "") => {
  const source = String(text);
  const characters = [];

  let pendingSpace = false;
  let lastEmittedWasDigit = false;

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];
    const characterClass = classify(character);

    if (characterClass === SPACE) {
      pendingSpace = characters.length > 0;
      continue;
    }

    const isAlphanumeric =
      characterClass === DIGIT || characterClass === LETTER;
    const isDigitFlanked =
      !isAlphanumeric &&
      lastEmittedWasDigit &&
      index + 1 < source.length &&
      classify(source[index + 1]) === DIGIT;

    if (!isAlphanumeric && !isDigitFlanked) {
      continue;
    }

    if (pendingSpace) {
      characters.push(" ");
      pendingSpace = false;
    }

    characters.push(isDigitFlanked ? character : character.toLowerCase());
    lastEmittedWasDigit = characterClass === DIGIT;
  }

  return characters.join("");
};

export default normalizeAnchorText;
